import type {RouterScrollBehavior} from 'vue-router';

type HashScrollOptions = {
  attempts?: number;
  delay?: number;
  offset?: number;
};

const getScrollParent = (element: HTMLElement): HTMLElement | Window => {
  let parent = element.parentElement;

  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;

    if (/(auto|scroll|overlay)/.test(overflowY) && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }

    parent = parent.parentElement;
  }

  return window;
};

const scrollElementIntoView = (target: HTMLElement, offset: number): void => {
  const scrollParent = getScrollParent(target);

  if (scrollParent === window) {
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({top, behavior: 'smooth'});
    return;
  }

  const parentRect = scrollParent.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const top = targetRect.top - parentRect.top + scrollParent.scrollTop - offset;

  scrollParent.scrollTo({top, behavior: 'smooth'});
};

export const scrollToHashTarget = (
  hash: string,
  options: HashScrollOptions = {},
): void => {
  if (!hash) {
    return;
  }

  const attempts = options.attempts ?? 10;
  const delay = options.delay ?? 75;
  const offset = options.offset ?? 84;
  const targetId = decodeURIComponent(hash.slice(1));
  let currentAttempt = 0;

  const tryScroll = (): void => {
    const target = document.getElementById(targetId);

    currentAttempt += 1;

    if (target) {
      scrollElementIntoView(target, offset);
      return;
    }

    if (currentAttempt < attempts) {
      window.setTimeout(tryScroll, delay);
    }
  };

  window.setTimeout(tryScroll, 0);
};

export function createHashScrollBehavior(options: HashScrollOptions = {}): RouterScrollBehavior {
  const attempts = options.attempts ?? 10;
  const delay = options.delay ?? 75;
  const offset = options.offset ?? 84;

  return (to, _from, savedPosition) => {
    if (savedPosition) {
      return savedPosition;
    }

    if (!to.hash) {
      return {top: 0};
    }

    return new Promise((resolve) => {
      let currentAttempt = 0;

      const tryScroll = (): void => {
        const target = document.getElementById(decodeURIComponent(to.hash.slice(1)));

        currentAttempt += 1;

        if (target) {
          scrollElementIntoView(target, offset);
          resolve(false);
          return;
        }

        if (currentAttempt >= attempts) {
          resolve(false);
          return;
        }

        window.setTimeout(tryScroll, delay);
      };

      window.setTimeout(tryScroll, 0);
    });
  };
}
